import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, CircularProgress, Alert, Chip, Card, CardContent, Grid
} from '@mui/material';
import { API_BASE_URL } from '../config/api';
import { v5Service } from '../services/v5Service';
import RecyclingIcon from '@mui/icons-material/Recycling';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BalanceIcon from '@mui/icons-material/Balance';

function Reports() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ledgerSummary, setLedgerSummary] = useState({ ledgers: [], totals: {} });
  const [allocationCoverage, setAllocationCoverage] = useState({ coverage: [], overall_compliance: {} });
  const [transferHistory, setTransferHistory] = useState({ transfers: [], summary: {} });
  const [v5, setV5] = useState({ circularity: [], water: [], checks: [] });

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const [summaryRes, coverageRes, transfersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/reports/ledger-summary`),
        fetch(`${API_BASE_URL}/api/reports/allocation-coverage`),
        fetch(`${API_BASE_URL}/api/reports/transfer-history`)
      ]);
      const summary = await summaryRes.json();
      const coverage = await coverageRes.json();
      const transfers = await transfersRes.json();
      setLedgerSummary(summary ?? { ledgers: [], totals: {} });
      setAllocationCoverage(coverage ?? { coverage: [], overall_compliance: {} });
      setTransferHistory(transfers ?? { transfers: [], summary: {} });
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
    // v5 circularity/water is optional — a missing OFP v5 dataset must not
    // break the core reports.
    const [circ, water, checks] = await Promise.allSettled([
      v5Service.getCircularitySummary(),
      v5Service.getWaterSummary(),
      v5Service.getConservationChecks(),
    ]);
    setV5({
      circularity: circ.status === 'fulfilled' ? circ.value : [],
      water: water.status === 'fulfilled' ? water.value : [],
      checks: checks.status === 'fulfilled' ? checks.value : [],
    });
  };

  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Reports</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        E-ledger accounting reports and compliance status
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Ledger Summary" />
          <Tab label="Allocation Compliance" />
          <Tab label="Transfer History" />
          <Tab label="Circularity & Water (v5)" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Ledgers</Typography>
                  <Typography variant="h4">{ledgerSummary?.totals?.total_ledgers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Emissions</Typography>
                  <Typography variant="h4">{(ledgerSummary?.totals?.total_debit_kg_co2e ?? 0).toFixed(2)}</Typography>
                  <Typography variant="caption">kg CO2e</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Allocated</Typography>
                  <Typography variant="h4">{(ledgerSummary?.totals?.total_credit_kg_co2e ?? 0).toFixed(2)}</Typography>
                  <Typography variant="caption">kg CO2e</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Fully Allocated</Typography>
                  <Typography variant="h4">{ledgerSummary?.totals?.fully_allocated_count ?? 0}</Typography>
                  <Typography variant="caption">ledgers</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ledger Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Emissions</TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Allocation %</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(ledgerSummary?.ledgers ?? []).map((ledger) => (
                  <TableRow key={ledger.e_ledger_id}>
                    <TableCell>{ledger.ledger_name}</TableCell>
                    <TableCell><Chip label={ledger.entity_type} size="small" /></TableCell>
                    <TableCell align="right">{(ledger.total_debit_kg_co2e ?? 0).toFixed(4)}</TableCell>
                    <TableCell align="right">{(ledger.total_credit_kg_co2e ?? 0).toFixed(4)}</TableCell>
                    <TableCell align="right" sx={{ color: ledger.is_fully_allocated ? 'success.main' : 'warning.main' }}>
                      {(ledger.balance_kg_co2e ?? 0).toFixed(4)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${ledger.allocation_percentage ?? 0}%`} 
                        size="small" 
                        color={parseFloat(ledger.allocation_percentage) === 100 ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={ledger.status ?? 'unknown'} size="small" color={ledger.status === 'active' ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Alert 
            severity={(allocationCoverage?.overall_compliance?.compliant_ledgers ?? 0) === (allocationCoverage?.overall_compliance?.total_ledgers ?? 0) ? 'success' : 'warning'}
            sx={{ mb: 3 }}
          >
            <Typography variant="h6">
              Compliance Rate: {allocationCoverage?.overall_compliance?.overall_allocation_rate ?? 0}%
            </Typography>
            <Typography>
              {(allocationCoverage?.overall_compliance?.compliant_ledgers ?? 0)} of {(allocationCoverage?.overall_compliance?.total_ledgers ?? 0)} ledgers are fully allocated per E-ledger principles.
            </Typography>
          </Alert>

          <Paper sx={{ p: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ledger</TableCell>
                  <TableCell align="right">Total Emissions</TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">Unallocated</TableCell>
                  <TableCell>Coverage</TableCell>
                  <TableCell>Compliance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(allocationCoverage?.coverage ?? []).map((item) => (
                  <TableRow key={item.e_ledger_id}>
                    <TableCell>{item.ledger_name}</TableCell>
                    <TableCell align="right">{(item.total_emissions_kg_co2e ?? 0).toFixed(4)}</TableCell>
                    <TableCell align="right">{(item.allocated_kg_co2e ?? 0).toFixed(4)}</TableCell>
                    <TableCell align="right" sx={{ color: (item.unallocated_kg_co2e ?? 0) > 0 ? 'warning.main' : 'success.main' }}>
                      {(item.unallocated_kg_co2e ?? 0).toFixed(4)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${item.allocation_percentage ?? 0}%`} 
                        color={parseFloat(item.allocation_percentage) === 100 ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.is_compliant ? 'Compliant' : 'Non-Compliant'} 
                        color={item.is_compliant ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tabValue === 2 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary">Total Transfers</Typography>
                  <Typography variant="h4">{transferHistory?.summary?.total_transfers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary">Total Transferred</Typography>
                  <Typography variant="h4">{(transferHistory?.summary?.total_transferred_kg_co2e ?? 0).toFixed(2)}</Typography>
                  <Typography variant="caption">kg CO2e</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary">Verified</Typography>
                  <Typography variant="h4" color="success.main">{transferHistory?.summary?.verified_transfers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary">Pending</Typography>
                  <Typography variant="h4" color="warning.main">{transferHistory?.summary?.pending_transfers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">kg CO2e</TableCell>
                  <TableCell>Legal Reference</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(transferHistory?.transfers ?? []).map((transfer) => (
                  <TableRow key={transfer.transfer_id}>
                    <TableCell>{transfer.transfer_date}</TableCell>
                    <TableCell>{transfer.fromLedger?.ledger_name || 'N/A'}</TableCell>
                    <TableCell>{transfer.toLedger?.ledger_name || 'End Consumer'}</TableCell>
                    <TableCell>{transfer.product_name || 'N/A'}</TableCell>
                    <TableCell align="right">{(parseFloat(transfer.transferred_liability_kg_co2e) ?? 0).toFixed(4)}</TableCell>
                    <TableCell>{transfer.legal_transfer_reference ?? 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transfer.verification_status ?? 'unknown'} 
                        size="small"
                        color={transfer.verification_status === 'verified' ? 'success' : transfer.verification_status === 'pending' ? 'warning' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
      {tabValue === 3 && (
        <Box>
          {v5.circularity.length === 0 && v5.water.length === 0 ? (
            <Alert severity="info">No v5 circularity or water data available.</Alert>
          ) : (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} color="text.secondary">
                        <RecyclingIcon sx={{ color: '#2e7d32' }} /> <Typography variant="caption">Avg circular inflow (CR_IN)</Typography>
                      </Box>
                      <Typography variant="h4">
                        {v5.circularity.length
                          ? (v5.circularity.reduce((a, o) => a + Number(o.avg_cr_in || 0), 0) / v5.circularity.length).toFixed(1)
                          : '0.0'}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} color="text.secondary">
                        <RecyclingIcon sx={{ color: '#00897b' }} /> <Typography variant="caption">Avg recycling rate (RR)</Typography>
                      </Box>
                      <Typography variant="h4">
                        {v5.circularity.length
                          ? (v5.circularity.reduce((a, o) => a + Number(o.avg_rr || 0), 0) / v5.circularity.length).toFixed(1)
                          : '0.0'}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} color="text.secondary">
                        <WaterDropIcon sx={{ color: '#1976d2' }} /> <Typography variant="caption">Water withdrawal</Typography>
                      </Box>
                      <Typography variant="h4">
                        {fmt(v5.water.reduce((a, o) => a + Number(o.withdrawal_m3 || 0), 0))} m³
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} color="text.secondary">
                        <BalanceIcon sx={{ color: '#5d4037' }} /> <Typography variant="caption">Conservation checks holding</Typography>
                      </Box>
                      <Typography variant="h4">
                        {v5.checks.length ? `${v5.checks.filter((c) => c.conservation_holds).length}/${v5.checks.length}` : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Circularity by Organization</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Organization</TableCell>
                          <TableCell align="right">Input (kg)</TableCell>
                          <TableCell align="right">Circular in (kg)</TableCell>
                          <TableCell align="right">Output (kg)</TableCell>
                          <TableCell align="right">Circular out (kg)</TableCell>
                          <TableCell align="right">Waste (kg)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {v5.circularity.map((o) => (
                          <TableRow key={o.organization_id} hover>
                            <TableCell>{o.organization_name}</TableCell>
                            <TableCell align="right">{fmt(o.input_kg)}</TableCell>
                            <TableCell align="right">{fmt(o.circular_in_kg)}</TableCell>
                            <TableCell align="right">{fmt(o.output_kg)}</TableCell>
                            <TableCell align="right">{fmt(o.circular_out_kg)}</TableCell>
                            <TableCell align="right">{fmt(o.waste_kg)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Water by Organization (m³)</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Organization</TableCell>
                          <TableCell align="right">Withdrawal</TableCell>
                          <TableCell align="right">Discharge</TableCell>
                          <TableCell align="right">Recycled</TableCell>
                          <TableCell align="right">Stressed sites</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {v5.water.map((o) => (
                          <TableRow key={o.organization_id} hover>
                            <TableCell>{o.organization_name}</TableCell>
                            <TableCell align="right">{fmt(o.withdrawal_m3)}</TableCell>
                            <TableCell align="right">{fmt(o.discharge_m3)}</TableCell>
                            <TableCell align="right">{fmt(o.recycle_m3)}</TableCell>
                            <TableCell align="right">{Number(o.stressed_facilities) > 0
                              ? <Chip size="small" color="error" label={o.stressed_facilities} />
                              : 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Conservation Checks (v5)</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Organization</TableCell>
                          <TableCell>Resource</TableCell>
                          <TableCell align="right">Inflow</TableCell>
                          <TableCell align="right">Outflow</TableCell>
                          <TableCell align="right">Net accumulated</TableCell>
                          <TableCell align="right">Expected</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Balance check</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {v5.checks.map((c) => (
                          <TableRow key={c.check_id} hover>
                            <TableCell>{c.organization_name || '—'}</TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" label={c.resource_type}
                                color={c.resource_type === 'material' ? 'primary' : 'info'} />
                            </TableCell>
                            <TableCell align="right">{fmt(c.total_inflow_quantity)}</TableCell>
                            <TableCell align="right">{fmt(c.total_outflow_quantity)}</TableCell>
                            <TableCell align="right">{fmt(c.net_accumulated_quantity)}</TableCell>
                            <TableCell align="right">{fmt(c.expected_accumulated_quantity)}</TableCell>
                            <TableCell>{(c.unit_of_measure_id || '').replace('uom-', '')}</TableCell>
                            <TableCell>
                              <Chip size="small" color={c.conservation_holds ? 'success' : 'error'}
                                label={c.conservation_holds ? 'Holds' : 'Violation'} />
                            </TableCell>
                          </TableRow>
                        ))}
                        {!v5.checks.length && (
                          <TableRow><TableCell colSpan={8}>No conservation checks found.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default Reports;
